import { useEffect, useMemo } from "react"
import {
  Calendar,
  Clock,
  Globe,
  Video,
  Download,
  CalendarPlus,
  CheckCircle,
  ArrowLeft,
  Mail,
} from "lucide-react"

/**
 * Parse booking details from the URL.
 *
 * Zoho Bookings appends booking info as query params on its "Show your own
 * confirmation page" redirect. The exact key names vary between Zoho plans
 * and aren't publicly documented, so we read several common aliases and
 * fall back gracefully when a field is missing.
 *
 * We check both `?key=val` (standard query string) and the portion of the
 * hash after `?` (since this app uses hash-based routing).
 */
function readBookingDetails(): Record<string, string> {
  const merged: Record<string, string> = {}

  const ingest = (sp: URLSearchParams) => {
    sp.forEach((value, key) => {
      if (value && !(key in merged)) merged[key] = value
    })
  }

  if (typeof window === "undefined") return merged

  ingest(new URLSearchParams(window.location.search))

  const hash = window.location.hash || ""
  const qIdx = hash.indexOf("?")
  if (qIdx >= 0) {
    ingest(new URLSearchParams(hash.slice(qIdx + 1)))
  }

  return merged
}

function firstDefined(obj: Record<string, string>, keys: string[]): string {
  for (const k of keys) {
    if (obj[k]) return obj[k]
  }
  return ""
}

function formatDateTime(raw: string): { date: string; time: string; iso: string } {
  if (!raw) return { date: "", time: "", iso: "" }
  let d: Date
  if (/^\d+$/.test(raw)) {
    const n = Number(raw)
    d = new Date(n < 1e12 ? n * 1000 : n)
  } else {
    d = new Date(raw.replace(" ", "T"))
  }
  if (isNaN(d.getTime())) return { date: raw, time: "", iso: "" }
  return {
    date: d.toLocaleDateString(undefined, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    time: d.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    }),
    iso: d.toISOString(),
  }
}

function toGCalDate(iso: string): string {
  return iso.replace(/[-:]|\.\d{3}/g, "")
}

function buildGoogleCalendarUrl(opts: {
  title: string
  startIso: string
  endIso: string
  details: string
  location: string
}): string {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: opts.title,
    dates: `${toGCalDate(opts.startIso)}/${toGCalDate(opts.endIso)}`,
    details: opts.details,
    location: opts.location,
  })
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

function buildIcsDataUrl(opts: {
  title: string
  startIso: string
  endIso: string
  description: string
  location: string
}): string {
  const dt = (iso: string) => toGCalDate(iso)
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Vorro//BridgeGate Demo//EN",
    "BEGIN:VEVENT",
    `UID:${Date.now()}@vorro.net`,
    `DTSTAMP:${dt(new Date().toISOString())}`,
    `DTSTART:${dt(opts.startIso)}`,
    `DTEND:${dt(opts.endIso)}`,
    `SUMMARY:${opts.title}`,
    `DESCRIPTION:${opts.description.replace(/\n/g, "\\n")}`,
    `LOCATION:${opts.location}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n")
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`
}

export function ThankYouPage() {
  // If we got here inside the Zoho iframe (because Zoho redirects the iframe,
  // not the parent), break out so the visitor sees the standalone page.
  useEffect(() => {
    try {
      if (window.top && window.top !== window.self) {
        window.top.location.href = window.location.href
      }
    } catch {
      /* cross-origin guard — best effort */
    }
  }, [])

  const details = useMemo(readBookingDetails, [])

  const customerName = firstDefined(details, [
    "customer_name",
    "name",
    "first_name",
  ])
  const customerEmail = firstDefined(details, ["customer_email", "email"])
  const serviceName = firstDefined(details, [
    "service_name",
    "service",
    "event_name",
  ])
  const staffName = firstDefined(details, [
    "staff_name",
    "consultant",
    "staff",
  ])
  const meetingUrl = firstDefined(details, [
    "meeting_url",
    "meeting_link",
    "location",
    "join_url",
  ])
  const timezone = firstDefined(details, ["timezone", "time_zone", "tz"])
  const bookingId = firstDefined(details, ["booking_id", "id", "appointment_id"])

  const startRaw = firstDefined(details, [
    "start_time",
    "booking_start_time",
    "start",
    "from_time",
  ])
  const endRaw = firstDefined(details, [
    "end_time",
    "booking_end_time",
    "end",
    "to_time",
  ])
  const start = formatDateTime(startRaw)
  const end = formatDateTime(endRaw)

  const hasMeetingDetails = Boolean(start.date || meetingUrl || serviceName)

  const eventTitle = serviceName || "Vorro BridgeGate Demo"
  const eventDescription = [
    "Vorro BridgeGate for n8n — demo session.",
    staffName ? `With: ${staffName}` : "",
    meetingUrl ? `Join: ${meetingUrl}` : "",
  ]
    .filter(Boolean)
    .join("\n")

  const gcalUrl =
    start.iso && end.iso
      ? buildGoogleCalendarUrl({
          title: eventTitle,
          startIso: start.iso,
          endIso: end.iso,
          details: eventDescription,
          location: meetingUrl || "Online",
        })
      : ""

  const icsUrl =
    start.iso && end.iso
      ? buildIcsDataUrl({
          title: eventTitle,
          startIso: start.iso,
          endIso: end.iso,
          description: eventDescription,
          location: meetingUrl || "Online",
        })
      : ""

  const goHome = () => {
    history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search,
    )
    window.location.hash = ""
  }

  const firstName = customerName ? customerName.split(" ")[0] : ""

  return (
    <div className="min-h-screen bg-white font-sans antialiased flex flex-col">
      <main className="relative flex-1 flex items-center overflow-hidden py-16 lg:py-24">
        {/* Soft pink hero glow */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#faf3f7] via-white to-[#f5f0f7]" />
        <div
          className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[480px] w-[480px] rounded-full opacity-40 blur-3xl"
          style={{
            background:
              "radial-gradient(closest-side, rgba(166,48,131,0.35), transparent)",
          }}
        />

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-2xl mx-auto text-center">
            {/* Glowing check badge */}
            <div className="relative inline-flex items-center justify-center w-24 h-24 md:w-28 md:h-28 rounded-full mb-8 animate-in fade-in zoom-in duration-500">
              <div className="absolute inset-0 rounded-full bg-[linear-gradient(135deg,#a63083,#bf406a)] opacity-15 blur-xl" />
              <div className="relative w-full h-full rounded-full bg-white border border-[#a63083]/20 shadow-[0_8px_30px_rgba(166,48,131,0.25)] flex items-center justify-center">
                <CheckCircle
                  className="h-12 w-12 md:h-14 md:w-14 text-[#a63083]"
                  strokeWidth={2.2}
                />
              </div>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-tight text-[#2d2d3a] mb-4">
              {firstName ? (
                <>
                  Thank you,{" "}
                  <span className="bg-[linear-gradient(135deg,#a63083,#bf406a)] bg-clip-text text-transparent">
                    {firstName}!
                  </span>
                </>
              ) : (
                <>
                  Your demo is{" "}
                  <span className="bg-[linear-gradient(135deg,#a63083,#bf406a)] bg-clip-text text-transparent">
                    confirmed!
                  </span>
                </>
              )}
            </h1>

            <p className="text-base md:text-lg text-[#6b6b7b] max-w-xl mx-auto mb-10 leading-relaxed">
              We've received your booking and our team will be in touch shortly.
              A calendar invite and confirmation email are on the way to your inbox.
            </p>

            {/* Booking details card */}
            {hasMeetingDetails && (
              <div className="bg-white/80 backdrop-blur border border-gray-100 rounded-2xl p-6 md:p-7 mb-10 text-left shadow-[0_8px_30px_rgba(45,45,58,0.06)] max-w-md mx-auto">
                <p className="text-xs font-semibold text-[#6b6b7b] uppercase tracking-wider mb-4">
                  Your demo details
                </p>
                <div className="flex flex-col gap-3.5">
                  {start.date && (
                    <div className="flex items-center gap-3 text-sm md:text-base text-[#2d2d3a]">
                      <Calendar size={18} className="text-[#a63083] shrink-0" />
                      <span className="font-medium">{start.date}</span>
                    </div>
                  )}
                  {start.time && (
                    <div className="flex items-center gap-3 text-sm md:text-base text-[#2d2d3a]">
                      <Clock size={18} className="text-[#a63083] shrink-0" />
                      <span>
                        {start.time}
                        {end.time && <span className="text-[#6b6b7b]"> – {end.time}</span>}
                      </span>
                    </div>
                  )}
                  {timezone && (
                    <div className="flex items-center gap-3 text-sm md:text-base text-[#2d2d3a]">
                      <Globe size={18} className="text-[#a63083] shrink-0" />
                      <span>{timezone}</span>
                    </div>
                  )}
                  {(serviceName || staffName) && (
                    <div className="flex items-center gap-3 text-sm md:text-base text-[#2d2d3a] pt-3 border-t border-gray-100">
                      <span className="inline-flex h-[18px] w-[18px] items-center justify-center shrink-0">
                        <span className="block h-2 w-2 rounded-full bg-[#a63083]" />
                      </span>
                      <span>
                        {serviceName || "Booking"}
                        {staffName ? ` · with ${staffName}` : ""}
                      </span>
                    </div>
                  )}
                  {meetingUrl && (
                    <div className="flex items-center gap-3 text-sm md:text-base text-[#2d2d3a] pt-3 border-t border-gray-100">
                      <Video size={18} className="text-[#a63083] shrink-0" />
                      <a
                        href={meetingUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#a63083] hover:underline break-all"
                      >
                        Join meeting
                      </a>
                    </div>
                  )}
                  {customerEmail && (
                    <div className="flex items-center gap-3 text-sm md:text-base text-[#6b6b7b] pt-3 border-t border-gray-100">
                      <Mail size={18} className="text-[#a63083] shrink-0" />
                      <span>Invite sent to {customerEmail}</span>
                    </div>
                  )}
                </div>

                {(gcalUrl || icsUrl) && (
                  <div className="mt-5 pt-5 border-t border-gray-100 flex flex-wrap gap-x-5 gap-y-2 justify-center text-sm">
                    {gcalUrl && (
                      <a
                        href={gcalUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-[#a63083] hover:underline font-medium"
                      >
                        <CalendarPlus className="h-4 w-4" />
                        Add to Google Calendar
                      </a>
                    )}
                    {icsUrl && (
                      <a
                        href={icsUrl}
                        download="vorro-demo.ics"
                        className="inline-flex items-center gap-2 text-[#a63083] hover:underline font-medium"
                      >
                        <Download className="h-4 w-4" />
                        Download ICS
                      </a>
                    )}
                  </div>
                )}

                {bookingId && (
                  <p className="text-xs text-[#6b6b7b] text-center mt-5">
                    Booking reference:{" "}
                    <span className="font-mono">{bookingId}</span>
                  </p>
                )}
              </div>
            )}

            {!hasMeetingDetails && (
              <p className="text-sm text-[#6b6b7b] mb-10">
                If you don't see it within a few minutes, please check your spam folder.
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={goHome}
                className="inline-flex items-center justify-center gap-2 bg-[linear-gradient(135deg,#a63083,#bf406a)] hover:opacity-90 text-white font-semibold px-8 h-12 rounded-xl shadow-[0_8px_24px_rgba(166,48,131,0.25)] transition-opacity"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </button>
              <a
                href="https://vorro.net"
                className="inline-flex items-center justify-center border border-[#a63083] text-[#a63083] hover:bg-[#a63083]/5 font-semibold px-8 h-12 rounded-xl transition-colors"
              >
                Explore Vorro
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
